import React from "react";

const DECISIONS = {
  approved: {
    shadowLabel: "Would approve",
    automaticLabel: "Approved and published",
    className: "border-emerald-300/40 bg-emerald-400/15 text-emerald-100",
  },
  changes_requested: {
    shadowLabel: "Would request changes",
    automaticLabel: "Changes required · not published",
    className: "border-amber-300/40 bg-amber-400/15 text-amber-100",
  },
  manual_review: {
    shadowLabel: "Would send to review",
    automaticLabel: "Manual review · not published",
    className: "border-orange-300/40 bg-orange-400/15 text-orange-100",
  },
  rejected: {
    shadowLabel: "Would reject",
    automaticLabel: "Rejected · not published",
    className: "border-rose-300/40 bg-rose-400/15 text-rose-100",
  },
};

const PREPARED_REJECTIONS = {
  not_group_activity: {
    actionLabel: "Reject: not a group activity",
    resolutionLabel: "Rejected · not a group activity",
    decisions: new Set(["rejected"]),
  },
  external_promotion: {
    actionLabel: "Reject: external promotion / ticketing",
    resolutionLabel: "Rejected · external promotion / ticketing",
    decisions: new Set(["manual_review", "rejected"]),
  },
};

function formatAssessmentTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function scoreTone(score, positive = false) {
  const numeric = Number(score || 0);
  if (positive) {
    if (numeric >= 75) return "text-emerald-200";
    if (numeric >= 50) return "text-amber-100";
    return "text-rose-200";
  }
  if (numeric >= 50) return "text-rose-200";
  if (numeric >= 25) return "text-amber-100";
  return "text-emerald-200";
}

export default function StaffEventModerationCard({
  assessment,
  onSendHostRules,
  sendingHostRules = false,
  onSendCommercialNotice,
  sendingCommercialNotice = false,
  onRejectWithTemplate,
  rejectingTemplateCode = "",
}) {
  if (!assessment) return null;

  const isAutomatic = assessment.mode === "automatic";
  const publishedAfterReview = isAutomatic && assessment.is_validated
    && assessment.proposed_decision !== "approved";
  const decision = publishedAfterReview
    ? DECISIONS.approved
    : DECISIONS[assessment.proposed_decision] || DECISIONS.manual_review;
  const decisionLabel = publishedAfterReview
    ? "Approved by staff · published"
    : isAutomatic ? decision.automaticLabel : decision.shadowLabel;
  const reasons = Array.isArray(assessment.reasons) ? assessment.reasons : [];
  const signals = assessment.signals && typeof assessment.signals === "object"
    ? assessment.signals
    : {};
  const strengths = Array.isArray(signals.strengths) ? signals.strengths : [];
  const host = signals.host || {};
  const participant = signals.participant || {};
  const financial = signals.financial || {};
  const staffResolution = assessment.staff_resolution || signals.staff_resolution || null;
  const isResolved = staffResolution?.status === "rejected";
  const isTerminalEventStatus = ["cancelled", "deleted", "finished", "expired_unvalidated"].includes(
    String(assessment.event_status || "").trim().toLowerCase()
  );
  const availableRejections = Object.entries(PREPARED_REJECTIONS).filter(([, template]) => (
    onRejectWithTemplate &&
    isAutomatic &&
    template.decisions.has(assessment.proposed_decision) &&
    !assessment.is_validated &&
    !isTerminalEventStatus &&
    !isResolved
  ));
  const isRejecting = Boolean(rejectingTemplateCode);
  const commercialReviewNeeded = reasons.some((reason) => (
    reason?.code === "ai_commercial_service_requires_manual_review"
  ));
  const commercialNoticeSent = Boolean(signals.staff_commercial_notice?.message_id);
  const canSendCommercialNotice = Boolean(
    onSendCommercialNotice && isAutomatic && commercialReviewNeeded &&
    assessment.proposed_decision === "manual_review" &&
    !assessment.is_validated && !isTerminalEventStatus && !isResolved &&
    !commercialNoticeSent
  );
  const resolutionLabel = PREPARED_REJECTIONS[staffResolution?.template_code]?.resolutionLabel
    || "Rejected · host notified";
  const assessedAt = formatAssessmentTime(assessment.assessed_at);

  return (
    <section
      className="mb-4 overflow-hidden rounded-2xl border border-[#81A7FF]/45 bg-[#18284D]/95 shadow-[0_12px_28px_rgba(0,0,0,0.24)]"
      aria-label="Internal event moderation assessment"
    >
      <div className="flex items-start justify-between gap-3 border-b border-white/10 bg-[#243B70]/80 px-4 py-3">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.13em] text-[#B7CAFF]">
            Moderation bot · {isAutomatic ? "automatic decision" : "shadow mode"}
          </p>
          <p className="mt-1 text-[13px] font-medium leading-[18px] text-white/78">
            {isResolved
              ? "The event was rejected and the host was notified."
              : isAutomatic
              ? (
                  assessment.is_validated
                    ? publishedAfterReview
                      ? "Automatic checks requested a review. Staff approved the event and it is published."
                      : "The event passed moderation and was published."
                    : "The event was not published. Staff action is required."
                )
              : "Internal preview only — the event and host are not affected."}
          </p>
        </div>
        <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-bold ${decision.className}`}>
          {decisionLabel}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 px-4 pt-3">
        <div className="rounded-xl border border-white/10 bg-black/15 px-3 py-2">
          <p className="text-[10px] font-bold uppercase tracking-wide text-white/50">Risk</p>
          <p className={`mt-0.5 text-[22px] font-black leading-6 ${scoreTone(assessment.risk_score)}`}>
            {Number(assessment.risk_score || 0)}<span className="text-[12px] text-white/50">/100</span>
          </p>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/15 px-3 py-2">
          <p className="text-[10px] font-bold uppercase tracking-wide text-white/50">Trust</p>
          <p className={`mt-0.5 text-[22px] font-black leading-6 ${scoreTone(assessment.trust_score, true)}`}>
            {Number(assessment.trust_score || 0)}<span className="text-[12px] text-white/50">/100</span>
          </p>
        </div>
      </div>

      {reasons.length ? (
        <div className="px-4 pt-3">
          <p className="text-[10px] font-bold uppercase tracking-wide text-white/50">
            {isAutomatic && !assessment.is_validated
              ? "Why it was not published"
              : publishedAfterReview ? "Automatic review signals" : "Why"}
          </p>
          <ul className="mt-1.5 space-y-1.5">
            {reasons.map((reason) => (
              <li key={`${reason?.code}-${reason?.title}`} className="rounded-lg bg-white/[0.06] px-2.5 py-2">
                <p className="text-[12px] font-bold leading-4 text-white">{reason?.title || "Review signal"}</p>
                {reason?.detail ? (
                  <p className="mt-0.5 text-[11px] leading-4 text-white/65">{reason.detail}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <details className="group mx-4 my-3 rounded-xl border border-white/10 bg-black/10">
        <summary className="cursor-pointer list-none px-3 py-2 text-[11px] font-semibold text-white/75">
          <span>Assessment data</span>
        </summary>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 border-t border-white/10 px-3 py-2.5 text-[11px] leading-4 text-white/65">
          <span>Host: {Number(host.successfully_finished || 0)} completed · {Number(host.cancelled || 0)} cancelled</span>
          <span>Participant rating: {Number(participant.rating_percent || 0)}%</span>
          <span>Reports: {Number(host.previous_event_reports || 0)}</span>
          <span>Warnings: {Number(host.active_moderation_warnings || 0)}</span>
          <span>Pricing: {financial.pricing_model || "free"}</span>
          <span>Payment total: {Number(financial.payment_amount_total || 0)}</span>
          {strengths.map((strength) => (
            <span key={strength?.code || strength?.title} className="col-span-2 text-emerald-100/90">
              {strength?.title}{strength?.detail ? ` — ${strength.detail}` : ""}
            </span>
          ))}
        </div>
      </details>

      <p className="px-4 pb-3 text-[10px] leading-4 text-white/42">
        Policy {assessment.policy_version || "auto-v1"}{assessedAt ? ` · assessed ${assessedAt}` : ""}
      </p>
      {onSendHostRules || availableRejections.length || canSendCommercialNotice || commercialNoticeSent || isResolved ? (
        <div className="border-t border-white/10 px-4 py-3">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-white/50">
            {isResolved ? "Resolution" : "Prepared responses"}
          </p>
          {isResolved ? (
            <div className="rounded-xl border border-rose-300/30 bg-rose-400/10 px-3 py-2.5 text-[12px] font-semibold leading-4 text-rose-100">
              {resolutionLabel}
            </div>
          ) : (
            <div className="space-y-2">
              {commercialNoticeSent ? (
                <div className="rounded-xl border border-emerald-300/30 bg-emerald-400/10 px-3 py-2.5 text-[12px] font-semibold leading-4 text-emerald-100">
                  Paid-event review message already sent
                </div>
              ) : null}
              {canSendCommercialNotice ? (
                <div className="space-y-1.5">
                  <p className="text-[11px] leading-4 text-white/65">
                    Check the event before sending the paid-event warning to its chat.
                  </p>
                  <button
                    type="button"
                    onClick={onSendCommercialNotice}
                    disabled={sendingCommercialNotice || sendingHostRules || isRejecting}
                    className="flex min-h-10 w-full items-center justify-center rounded-xl border border-amber-300/35 bg-amber-400/15 px-4 text-[12px] font-bold text-amber-50 transition hover:bg-amber-400/25 active:scale-[0.99] disabled:cursor-wait disabled:opacity-55"
                  >
                    {sendingCommercialNotice ? "Sending…" : "Send paid-event review message to chat"}
                  </button>
                </div>
              ) : null}
              {availableRejections.map(([templateCode, template]) => (
                <button
                  key={templateCode}
                  type="button"
                  onClick={() => onRejectWithTemplate(templateCode)}
                  disabled={isRejecting || sendingHostRules || sendingCommercialNotice}
                  className="flex min-h-10 w-full items-center justify-center rounded-xl border border-rose-300/35 bg-rose-400/15 px-4 text-[12px] font-bold text-rose-50 transition hover:bg-rose-400/25 active:scale-[0.99] disabled:cursor-wait disabled:opacity-55"
                >
                  {rejectingTemplateCode === templateCode ? "Rejecting…" : template.actionLabel}
                </button>
              ))}
              {onSendHostRules ? (
                <button
                  type="button"
                  onClick={onSendHostRules}
                  disabled={sendingHostRules || isRejecting || sendingCommercialNotice}
                  className="flex min-h-10 w-full items-center justify-center rounded-xl border border-[#AFC5FF]/35 bg-[#6F94FF]/20 px-4 text-[12px] font-bold text-white transition hover:bg-[#6F94FF]/30 active:scale-[0.99] disabled:cursor-wait disabled:opacity-55"
                >
                  {sendingHostRules ? "Sending…" : "Send hosting rules reminder"}
                </button>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}
